import React, { useEffect } from 'react';

import { Icon } from '@iconify/react';
import { styled, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';
import { useGrammarCheckerContext } from '../../context/GrammarCheckerProvider';
import { GrammarCheckAlert } from '../../types';
import getUnderlineColor from '../../utils/getUnderlineColor';
import {
  mapGrammarCategoryToTranslationKey,
  mapGrammarMessageToTranslationKey,
} from '../../utils/localizationMapper';

const BlackTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }}>
    {props.children}
  </Tooltip>
))(() => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#2D3130',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#2D3130',
    fontSize: '12px',
    color: '#F2F0F4',
    fontWeight: '400',
    borderRadius: '4px',
    padding: '4px 8px',
  },
}));

type Props = {
  alert: GrammarCheckAlert;
  alertRect?: DOMRect;
  onInsert?: (text: string) => void;
  onDismiss?: () => void;
  targetEl?: HTMLElement;
  containerEl?: HTMLElement | null;
  popoverProps?: {
    ref?: React.RefObject<HTMLDivElement>;
    style?: React.CSSProperties;
  };
};

const AlertPopover: React.FC<Props> = ({
  alert,
  alertRect,
  onInsert,
  onDismiss,
  targetEl,
  containerEl,
  popoverProps,
}) => {
  const { t } = useTranslation();
  const { analytics, errorLogger } = useGrammarCheckerContext();

  const isHoverPopover = Boolean(containerEl);

  useEffect(() => {
    if (isHoverPopover) {
      analytics.trackEvent('extension - grammar alert popover shown', {
        category: alert.category,
      });
    }
  }, []);

  const popoverStyle: React.CSSProperties = {
    ...(alertRect && targetEl
      ? {
          width: '240px',
          boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.4)',
          borderRadius: '8px',
        }
      : {}),
    ...popoverProps?.style,
  };

  return (
    <div ref={popoverProps?.ref} style={popoverStyle}>
      <div className={styles.main}>
        <div className={styles.flex_row}>
          <div className={clsx(styles.headline, styles.flex)}>
            <Icon
              icon="tabler:text-grammar"
              style={{
                color: getUnderlineColor(alert),
                fontSize: '15px',
                width: '15px',
              }}
            />
            &nbsp;
            {t(
              mapGrammarCategoryToTranslationKey(
                alert?.category || 'Other',
                errorLogger,
              ),
              '',
            ) || t('alertPopover.grammar_mistake', 'Grammar mistake')}
          </div>
          {onDismiss && (
            <BlackTooltip
              placement="top"
              title={t('alertPopover.dismiss', 'Dismiss')}
            >
              <button
                className={styles.dismiss}
                onPointerDown={(e): void => {
                  e.preventDefault();
                  analytics.trackEvent('extension - grammar alert dismissed', {
                    category: alert.category,
                  });
                  onDismiss();
                }}
              >
                <Icon
                  icon="tabler:trash"
                  style={{
                    fontSize: '18px',
                    width: '18px',
                  }}
                />
              </button>
            </BlackTooltip>
          )}
        </div>
        {alert.message && (
          <div className={styles.message}>
            {t(
              mapGrammarMessageToTranslationKey(alert.message, errorLogger),
              '',
            )}
          </div>
        )}
        {alert.replacements && alert.replacements.length > 0 && onInsert && (
          <div className={styles.flex_row}>
            <button
              className={styles.button}
              onPointerDown={(e): void => {
                e.preventDefault();
                onInsert(alert.replacements[0]);
                analytics.trackEvent('extension - grammar alert applied', {
                  category: alert.category,
                });
              }}
            >
              {alert.replacements[0]}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertPopover;
